import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import {
  Money as MoneyIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { RefundMethod } from '../../utils/types';

interface RefundMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (method: RefundMethod) => void;
  refundAmount: number;
  customerName?: string;
  customerCreditBalance?: number;
  loading?: boolean;
}

const RefundMethodDialog: React.FC<RefundMethodDialogProps> = ({
  open,
  onClose,
  onConfirm,
  refundAmount,
  customerName,
  customerCreditBalance = 0,
  loading = false
}) => {
  const [selectedMethod, setSelectedMethod] = useState<RefundMethod>(RefundMethod.CASH);

  const handleConfirm = () => {
    onConfirm(selectedMethod);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        borderRadius: '12px 12px 0 0'
      }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <AccountBalanceWalletIcon />
          <Typography variant="h6" fontWeight="bold">
            اختيار طريقة الاسترداد
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* معلومات المرتجع */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            <strong>مبلغ المرتجع:</strong> {formatCurrency(refundAmount)}
          </Typography>
          {customerName && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>العميل:</strong> {customerName}
            </Typography>
          )}
        </Alert>

        {/* رصيد العميل الحالي */}
        {customerName && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              رصيد العميل الحالي:
            </Typography>
            <Chip 
              label={formatCurrency(customerCreditBalance)}
              color={customerCreditBalance > 0 ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        )}

        {/* خيارات الاسترداد */}
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
            اختر طريقة الاسترداد:
          </FormLabel>
          
          <RadioGroup
            value={selectedMethod}
            onChange={(e) => setSelectedMethod(e.target.value as RefundMethod)}
          >
            {/* استرداد نقدي */}
            <FormControlLabel
              value={RefundMethod.CASH}
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <MoneyIcon color="success" />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      استرداد نقدي
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      إرجاع المبلغ نقداً من الخزينة
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{
                border: selectedMethod === RefundMethod.CASH ? '2px solid #4caf50' : '1px solid #e0e0e0',
                borderRadius: 2,
                p: 1,
                mb: 1,
                '&:hover': {
                  bgcolor: 'grey.50'
                }
              }}
            />

            {/* رصيد للعميل */}
            {customerName && (
              <FormControlLabel
                value={RefundMethod.CREDIT}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <AccountBalanceWalletIcon color="primary" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        رصيد للعميل
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        إضافة المبلغ كرصيد يمكن استخدامه في المشتريات القادمة
                      </Typography>
                      <Typography variant="caption" color="primary">
                        الرصيد الجديد سيكون: {formatCurrency(customerCreditBalance + refundAmount)}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{
                  border: selectedMethod === RefundMethod.CREDIT ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 1,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'grey.50'
                  }
                }}
              />
            )}
          </RadioGroup>
        </FormControl>

        {/* تنبيه للعملاء بدون حساب */}
        {!customerName && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              خيار الرصيد متاح فقط للعملاء المسجلين في النظام
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          sx={{ 
            minWidth: 120,
            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)'
            }
          }}
        >
          {loading ? 'جاري المعالجة...' : 'تأكيد الاسترداد'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundMethodDialog;
